import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { AlertTriangle, Wand2, CheckCircle2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { accountingApi, unitsApi } from '../lib/api';

const fmt$ = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const s = String(iso).split('T')[0];
  const [y, m, d] = s.split('-');
  if (y && m && d) return `${m}/${d}/${y}`;
  return s;
};

/**
 * Orphan Loads Panel — surfaces loads that were imported without a unit_id
 * (typically from Amazon Trip History or CSV imports).  Shows a banner + a
 * dialog with bulk-assign controls.
 */
export default function OrphanLoadsPanel({ onChanged }) {
  const [orphans, setOrphans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [targetUnit, setTargetUnit] = useState('');
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([accountingApi.listOrphanLoads(), unitsApi.getAll()]);
      setOrphans(r.data.orphans || []);
      setTotal(r.data.total_amount || 0);
      setUnits(Array.isArray(u.data) ? u.data : (u.data.units || []));
    } catch { /* silent — not critical */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAll = () => {
    if (selected.size === orphans.length) setSelected(new Set());
    else setSelected(new Set(orphans.map((o) => o.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const autoBackfill = async () => {
    setBusy(true);
    try {
      const res = await accountingApi.autoBackfillOrphans();
      const { assigned, still_unassigned, active_unit_count } = res.data;
      if (assigned > 0) {
        toast.success(`Auto-assigned ${assigned} load${assigned === 1 ? '' : 's'}`);
      } else {
        toast.info(
          active_unit_count > 1
            ? `Multiple active units (${active_unit_count}) — pick a unit below to bulk-assign the remaining ${still_unassigned} loads`
            : 'Nothing to auto-assign',
        );
      }
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-backfill failed'); }
    finally { setBusy(false); }
  };

  const assignSelected = async () => {
    if (!targetUnit) return toast.error('Pick a unit first');
    if (selected.size === 0) return toast.error('Select at least one load');
    setBusy(true);
    try {
      const res = await accountingApi.backfillOrphansToUnit({
        unit_id: targetUnit,
        load_ids: Array.from(selected),
      });
      toast.success(`${res.data.loads_updated} load(s) assigned to #${res.data.unit_number}${
        res.data.driver_backfilled ? ` · driver backfilled on ${res.data.driver_backfilled}` : ''
      }`);
      setSelected(new Set());
      setTargetUnit('');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };

  const assignAll = async () => {
    if (!targetUnit) return toast.error('Pick a unit first');
    if (!window.confirm(`Assign ALL ${orphans.length} unassigned loads to this unit? This cannot be undone in one click.`)) return;
    setBusy(true);
    try {
      const res = await accountingApi.backfillOrphansToUnit({
        unit_id: targetUnit,
        apply_to_all_orphans: true,
      });
      toast.success(`${res.data.loads_updated} load(s) assigned to #${res.data.unit_number}`);
      setSelected(new Set());
      setTargetUnit('');
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };

  if (loading || orphans.length === 0) return null;

  return (
    <>
      <Card className="border-amber-300 bg-amber-50/40 dark:bg-amber-950/20" data-testid="orphan-loads-panel">
        <CardContent className="p-3 flex flex-wrap items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">
              {orphans.length} load{orphans.length === 1 ? '' : 's'} without an assigned unit
              <Badge variant="outline" className="ml-2 text-xs">{fmt$(total)}</Badge>
            </div>
            <div className="text-xs text-muted-foreground">
              These are usually Amazon Trip History or CSV imports that arrived without a unit column. Assign them so they show up in Unit Breakdown and settlements.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={autoBackfill} disabled={busy} data-testid="orphan-auto-btn">
            <Wand2 className="h-3 w-3 mr-1" /> Auto-assign
          </Button>
          <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="orphan-review-btn">
            Review &amp; Assign
          </Button>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[900px] w-[92vw] max-h-[85vh] flex flex-col p-0" data-testid="orphan-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" /> Unassigned Loads
            </DialogTitle>
            <DialogDescription className="text-xs">
              Select the loads you want to assign, pick a target unit, then click Assign.
              If they all belong to the same truck, use &ldquo;Assign all&rdquo;.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <input type="checkbox"
                           checked={selected.size === orphans.length && orphans.length > 0}
                           onChange={toggleAll}
                           data-testid="orphan-select-all" />
                  </TableHead>
                  <TableHead>Load Ref</TableHead>
                  <TableHead>Broker</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orphans.map((o) => (
                  <TableRow key={o.id} data-testid={`orphan-row-${o.id}`}>
                    <TableCell>
                      <input type="checkbox"
                             checked={selected.has(o.id)}
                             onChange={() => toggleOne(o.id)}
                             data-testid={`orphan-select-${o.id}`} />
                    </TableCell>
                    <TableCell className="text-xs">{o.load_reference || o.load_number || o.id.slice(0, 8)}</TableCell>
                    <TableCell className="text-xs">{o.broker_name || o.broker || '—'}</TableCell>
                    <TableCell className="text-xs">
                      {fmtDate(o.start_date || o.pickup_date)}
                      {o.end_date && o.end_date !== o.start_date && <> → {fmtDate(o.end_date || o.delivery_date)}</>}
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{fmt$(o.payout_amount || o.rate || 0)}</TableCell>
                    <TableCell className="text-[10px] text-muted-foreground truncate max-w-[200px]">{o.notes || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DialogFooter className="p-3 border-t flex-wrap gap-2 items-center">
            <div className="text-xs text-muted-foreground mr-auto">
              {selected.size} of {orphans.length} selected
            </div>
            <Select value={targetUnit} onValueChange={setTargetUnit}>
              <SelectTrigger className="h-8 w-56 text-xs" data-testid="orphan-target-unit">
                <SelectValue placeholder="Assign to unit…" />
              </SelectTrigger>
              <SelectContent>
                {units.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    <Truck className="h-3 w-3 inline mr-1.5" />
                    #{u.unit_number}{u.make ? ` · ${u.make}` : ''}{u.model ? ` ${u.model}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={assignSelected} disabled={busy || !targetUnit || selected.size === 0}
                    data-testid="orphan-assign-selected">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Assign selected ({selected.size})
            </Button>
            <Button size="sm" onClick={assignAll} disabled={busy || !targetUnit}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="orphan-assign-all">
              Assign all {orphans.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
